import { ReactNode } from 'react'
import Head from 'next/head'
import Seo, { SITE_URL } from '@comp/seo'
import JsonLd from '@comp/structured-data'
import Layout from '@comp/layout'
import FinalCta from '@comp/home/final-cta'
import Container from '@comp/ui/container'
import Section from '@comp/ui/section'
import Eyebrow from '@comp/ui/eyebrow'
import Heading from '@comp/ui/heading'
import Text from '@comp/ui/text'
import Button from '@comp/ui/button'
import ComparisonTable from './comparison-table'
import { comparisonSchema, type ComparisonSource } from './comparison-data'

export interface VersusVendor {
  name: string
  summary: string
  strengths: string[]
  considerations: string[]
}

export interface VersusRow {
  sources?: ComparisonSource[]
  dimension: string
  detail?: string
  a: string
  b: string
  monofor: string
}

export interface VersusFaq {
  q: string
  a: string
}

const ui = {
  strengths: { en: 'Strengths', tr: 'G\u00fc\u00e7l\u00fc y\u00f6nler' },
  considerations: { en: 'Considerations', tr: 'Dikkat edilecekler' },
  sideBySide: { en: 'Side by side', tr: 'Yan yana' },
  dimensionTitle: { en: 'Dimension by dimension.', tr: 'Boyut boyut kar\u015f\u0131la\u015ft\u0131rma.' },
  chooseIf: { en: (name: string) => `Choose ${name} if\u2026`, tr: (name: string) => `${name} tercih edin, e\u011fer\u2026` },
  thirdOption: { en: 'The third option', tr: '\u00dc\u00e7\u00fcnc\u00fc se\u00e7enek' },
  startTrial: { en: 'Start free trial \u2192', tr: '\u00dccretsiz deneme ba\u015flat \u2192' },
  talkExperts: { en: 'Talk with our experts', tr: 'Uzmanlar\u0131m\u0131zla g\u00f6r\u00fc\u015f\u00fcn' },
  faqEyebrow: { en: 'FAQ', tr: 'SSS' },
  faqTitle: { en: 'Common questions.', tr: 'S\u0131k sorulan sorular.' },
  noteA: {
    en: 'Comparison based on publicly available information as of July 2026.',
    tr: 'Kar\u015f\u0131la\u015ft\u0131rma, Temmuz 2026 itibar\u0131yla kamuya a\u00e7\u0131k bilgilere dayanmaktad\u0131r.',
  },
  noteB: {
    en: 'are trademarks of their respective owners; Monofor is not affiliated with or endorsed by either. Spotted something out of date?',
    tr: 'ilgili sahiplerinin ticari markalar\u0131d\u0131r; Monofor\u2019un bunlarla ba\u011flant\u0131s\u0131 veya onay ili\u015fkisi yoktur. G\u00fcncelli\u011fini yitiren bir \u015fey mi g\u00f6rd\u00fcn\u00fcz?',
  },
  tellUs: { en: 'Tell us', tr: 'Bize yaz\u0131n' },
}

interface VersusPageProps {
  /** Page locale; pass from getStaticProps for bilingual pages. */
  locale?: string
  /** When true, emits EN/TR hreflang alternates and locale-aware canonical. */
  bilingual?: boolean
  path: string
  seoTitle: string
  seoDescription: string
  heroEyebrow: string
  heroTitle: ReactNode
  heroBody: string
  vendorA: VersusVendor
  vendorB: VersusVendor
  rows: VersusRow[]
  whenA: string[]
  whenB: string[]
  thirdOptionTitle: ReactNode
  thirdOptionBody: string
  thirdOptionPoints: string[]
  faqs: VersusFaq[]
  comparisonNote?: string
}

export default function VersusPage({
  locale = 'en',
  bilingual = false,
  path,
  seoTitle,
  seoDescription,
  heroEyebrow,
  heroTitle,
  heroBody,
  vendorA,
  vendorB,
  rows,
  whenA,
  whenB,
  thirdOptionTitle,
  thirdOptionBody,
  thirdOptionPoints,
  faqs,
  comparisonNote,
}: VersusPageProps) {
  const lang = locale === 'tr' ? 'tr' : 'en'
  const basePath = bilingual && lang === 'tr' ? '/tr' : ''
  const columns = [{ name: vendorA.name }, { name: vendorB.name }, { name: 'Monofor', highlight: true }]
  const entries = rows.map(row => ({ feature: row.dimension, detail: row.detail, sources: row.sources, values: [row.a, row.b, row.monofor] }))
  const caption = `${vendorA.name} / ${vendorB.name} / Monofor: ${lang === 'tr' ? 'özellik karşılaştırması' : 'capability comparison'}`
  return (
    <Layout>
      <Seo title={seoTitle} description={seoDescription} path={`${basePath}${path}`} />
      {bilingual && (
        <Head>
          <link rel="alternate" hrefLang="en" href={`${SITE_URL}${path}`} />
          <link rel="alternate" hrefLang="tr" href={`${SITE_URL}/tr${path}`} />
          <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}${path}`} />
        </Head>
      )}
      <JsonLd id="comparison-page" data={comparisonSchema({ url: `${SITE_URL}${basePath}${path}`, title: seoTitle, description: seoDescription, caption, lang, columns, rows: entries })} />
      <JsonLd
        id="versus-faq"
        data={{
          '@type': 'FAQPage',
          '@id': `${SITE_URL}${basePath}${path}#faq`,
          inLanguage: lang,
          mainEntity: faqs.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        }}
      />
      <main className="bg-surface text-ink-primary" lang={lang}>
        {/* Hero */}
        <Section tone="default" density="compact" className="relative overflow-hidden">
          <div aria-hidden className="pointer-events-none absolute inset-0">
            <div className="absolute -top-32 -right-24 h-[640px] w-[640px] rounded-full bg-brand-100/60 blur-3xl" />
            <div className="absolute top-40 -left-32 h-[420px] w-[420px] rounded-full bg-accent-100/50 blur-3xl" />
          </div>
          <Container size="xl" className="relative">
            <div className="max-w-3xl">
              <Eyebrow tone="brand">{heroEyebrow}</Eyebrow>
              <div className="mt-6">
                <Heading as="h1" size="display-xl">
                  {heroTitle}
                </Heading>
              </div>
              <div className="mt-8 max-w-2xl">
                <Text size="xl" tone="secondary">
                  {heroBody}
                </Text>
              </div>
            </div>
          </Container>
        </Section>

        {/* Vendor overviews */}
        <Section tone="subtle" density="compact">
          <Container size="xl">
            <div className="grid gap-6 lg:grid-cols-2">
              {[vendorA, vendorB].map((vendor) => (
                <div
                  key={vendor.name}
                  className="rounded-3xl border border-line-subtle bg-surface p-8 shadow-card lg:p-10"
                >
                  <h2 className="text-display-sm font-medium text-ink-primary">
                    {vendor.name}
                  </h2>
                  <Text size="md" tone="secondary" className="mt-4">
                    {vendor.summary}
                  </Text>
                  <div className="mt-6 grid gap-6 sm:grid-cols-2">
                    <div>
                      <div className="text-eyebrow font-semibold uppercase tracking-[0.12em] text-accent-700">
                        {ui.strengths[lang]}
                      </div>
                      <ul className="mt-3 space-y-2">
                        {vendor.strengths.map((s) => (
                          <li
                            key={s}
                            className="text-body-sm leading-relaxed text-ink-secondary"
                          >
                            {s}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <div className="text-eyebrow font-semibold uppercase tracking-[0.12em] text-ink-tertiary">
                        {ui.considerations[lang]}
                      </div>
                      <ul className="mt-3 space-y-2">
                        {vendor.considerations.map((c) => (
                          <li
                            key={c}
                            className="text-body-sm leading-relaxed text-ink-secondary"
                          >
                            {c}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Container>
        </Section>

        {/* Dimension table */}
        <Section tone="default" density="default">
          <Container size="xl">
            <div className="max-w-3xl">
              <Eyebrow tone="accent">{ui.sideBySide[lang]}</Eyebrow>
              <Heading as="h2" size="display-lg" className="mt-5">
                {ui.dimensionTitle[lang]}
              </Heading>
            </div>

            <ComparisonTable columns={columns} rows={entries} caption={caption} lang={lang} />

            <div className="mt-6 text-body-sm text-ink-tertiary">
              {comparisonNote || ui.noteA[lang]} {vendorA.name} {lang === 'tr' ? 've' : 'and'}{' '}
              {vendorB.name} {ui.noteB[lang]}{' '}
              <a
                href="mailto:hello@monofor.com"
                className="underline underline-offset-4 hover:text-ink-secondary"
              >
                {ui.tellUs[lang]}
              </a>
              .
            </div>
          </Container>
        </Section>

        {/* When to choose */}
        <Section tone="subtle" density="compact">
          <Container size="xl">
            <div className="grid gap-6 lg:grid-cols-2">
              <div className="rounded-3xl border border-line-subtle bg-surface p-8 lg:p-10">
                <h2 className="text-display-sm font-medium text-ink-primary">
                  {ui.chooseIf[lang](vendorA.name)}
                </h2>
                <ul className="mt-5 space-y-3">
                  {whenA.map((w) => (
                    <li
                      key={w}
                      className="text-body-md leading-relaxed text-ink-secondary"
                    >
                      {w}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-3xl border border-line-subtle bg-surface p-8 lg:p-10">
                <h2 className="text-display-sm font-medium text-ink-primary">
                  {ui.chooseIf[lang](vendorB.name)}
                </h2>
                <ul className="mt-5 space-y-3">
                  {whenB.map((w) => (
                    <li
                      key={w}
                      className="text-body-md leading-relaxed text-ink-secondary"
                    >
                      {w}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Container>
        </Section>

        {/* Third option */}
        <Section tone="inverse" density="default">
          <Container size="xl">
            <div className="max-w-3xl">
              <Eyebrow tone="accent">{ui.thirdOption[lang]}</Eyebrow>
              <Heading as="h2" size="display-lg" tone="inverse" className="mt-5">
                {thirdOptionTitle}
              </Heading>
              <Text size="xl" className="mt-6 !text-brand-100">
                {thirdOptionBody}
              </Text>
            </div>
            <div className="mt-10 grid gap-4 sm:grid-cols-3">
              {thirdOptionPoints.map((point) => (
                <div
                  key={point}
                  className="rounded-2xl border border-white/10 bg-white/5 p-6 text-body-md text-brand-100"
                >
                  {point}
                </div>
              ))}
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Button href="/try/now" variant="primary" size="lg" className="!bg-white !text-brand-900 hover:!bg-brand-50">
                {ui.startTrial[lang]}
              </Button>
              <Button href="/contact" variant="outline" size="lg" className="!border-white/30 !text-white hover:!bg-white/10">
                {ui.talkExperts[lang]}
              </Button>
            </div>
          </Container>
        </Section>

        {/* FAQ */}
        <Section id="faq" tone="subtle" density="default">
          <Container size="md">
            <Eyebrow tone="brand">{ui.faqEyebrow[lang]}</Eyebrow>
            <Heading as="h2" size="display-lg" className="mt-5">
              {ui.faqTitle[lang]}
            </Heading>
            <dl className="mt-12 space-y-4">
              {faqs.map((faq) => (
                <div
                  key={faq.q}
                  className="rounded-2xl border border-line-subtle bg-surface p-6 lg:p-8"
                >
                  <dt className="text-body-lg font-medium text-ink-primary">
                    {faq.q}
                  </dt>
                  <dd className="mt-3 text-body-md text-ink-secondary">
                    {faq.a}
                  </dd>
                </div>
              ))}
            </dl>
          </Container>
        </Section>

        <FinalCta />
      </main>
    </Layout>
  )
}
